import { useMemo } from "react";

import { NullAnswers } from "../../components/EvaluationUpdateForm/EvaluationUpdateForm.decl";
import {
  extractCategoryAndQuestionIndexes,
  extractQuestions,
  getHighestPoints,
  isBinaryEvaluationQuestion,
  isRatingEvaluationQuestion,
} from "../../components/EvaluationUpdateForm/EvaluationUpdateForm.utility";

import {
  BinaryEvaluationQuestion,
  RatingEvaluationQuestion,
} from "@/graphql/generated/graphql";
import { useEvaluationUpdateForm } from "@/hooks/useEvaluationUpdateForm/useEvaluationUpdateForm";

function computeBinaryAnswerTotal(
  total: number,
  question: BinaryEvaluationQuestion
) {
  if (
    question.binaryAnswer === "NOT_APPLICABLE" ||
    question.binaryAnswer === null
  ) {
    return total;
  }

  return (
    total +
    (question.questionPossibleAnswers.find(
      (answer) => answer.binaryValue === question.binaryAnswer
    )?.points ?? 0)
  );
}

function computeRatingAnswerTotal(
  total: number,
  question: RatingEvaluationQuestion
) {
  if (
    question.ratingAnswer === "NOT_APPLICABLE" ||
    question.ratingAnswer === null
  ) {
    return total;
  }

  return (
    total +
    (question.questionPossibleAnswers.find(
      (answer) => answer.ratingValue === question.ratingAnswer
    )?.points ?? 0)
  );
}

function computeBinaryNotApplicablePoints(
  total: number,
  question: BinaryEvaluationQuestion
) {
  if (question.binaryAnswer !== "NOT_APPLICABLE") {
    return total;
  }

  return total + question.questionPossibleAnswers.reduce(getHighestPoints, 0);
}

function computeRatingNotApplicablePoints(
  total: number,
  question: RatingEvaluationQuestion
) {
  if (question.ratingAnswer !== "NOT_APPLICABLE") {
    return total;
  }

  return total + question.questionPossibleAnswers.reduce(getHighestPoints, 0);
}

export function useEvaluationUpdateFormInteractions() {
  const { form } = useEvaluationUpdateForm();

  const overallScore = form.useStore((state) =>
    state.values.categories.reduce(
      (total, category) =>
        total +
        category.questions.reduce((categoryTotal, question) => {
          if (isBinaryEvaluationQuestion(question)) {
            return computeBinaryAnswerTotal(categoryTotal, question);
          }

          if (isRatingEvaluationQuestion(question)) {
            return computeRatingAnswerTotal(categoryTotal, question);
          }

          return categoryTotal;
        }, 0),
      0
    )
  );

  const notApplicablePointsPerCategory = form.useStore((state) =>
    state.values.categories.flatMap((category) =>
      category.questions.reduce((accu, question) => {
        if (isBinaryEvaluationQuestion(question)) {
          return computeBinaryNotApplicablePoints(accu, question);
        }

        if (isRatingEvaluationQuestion(question)) {
          return computeRatingNotApplicablePoints(accu, question);
        }

        return accu;
      }, 0)
    )
  );

  const maximumPointsByCategory = form.useStore((state) =>
    state.values.categories.flatMap((category) =>
      category.questions.reduce(
        (accu, question) =>
          accu + question.questionPossibleAnswers.reduce(getHighestPoints, 0),
        0
      )
    )
  );

  const overallMaxScore = useMemo(() => {
    const withoutNotApplicableQuestionPoints = maximumPointsByCategory.map(
      (points, index) =>
        points - (notApplicablePointsPerCategory.at(index) ?? 0)
    );

    return withoutNotApplicableQuestionPoints.reduce(
      (accu, points) => accu + points,
      0
    );
  }, [maximumPointsByCategory, notApplicablePointsPerCategory]);

  // If we have nullAnswers we should not be able to submit the form.
  // [2, [0, 1, 2]] means that the category at index 2 has null questions at index 0, 1, 2.
  const nullAnswers: NullAnswers = form.useStore((state) =>
    state.values.categories.flatMap(extractCategoryAndQuestionIndexes)
  );

  const hasUnansweredQuestions = useMemo(
    () => nullAnswers.flatMap(extractQuestions).length,
    [nullAnswers]
  );

  return {
    overallScore,
    overallMaxScore,
    nullAnswers,
    hasUnansweredQuestions,
  };
}
